
import { T } from "@/i18n/T";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Lightbulb, AlertTriangle, Zap } from "lucide-react";
import { TLDRAnalysis } from "@/hooks/useArticleAI";

interface TLDRWidgetProps {
  data: TLDRAnalysis | null;
  loading: boolean;
  isRTL?: boolean;
}

const TLDRWidget = ({ data, loading, isRTL = false }: TLDRWidgetProps) => {
  if (loading) {
    return (
      <>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="h-5 w-5 text-orange-500" />
              <T k="ai.tldr" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-full mb-1" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="h-5 w-5 text-rank-gold" />
              <T k="ai.keyPoints" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Skeleton className="h-3 w-full mb-1" />
            <Skeleton className="h-3 w-full mb-1" />
            <Skeleton className="h-3 w-2/3" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-blue-500" />
              <T k="ai.simplified" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-full mb-1" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <T k="ai.challengeThis" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-full mb-1" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      </>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-byteline-green" />
            <T k="auto.ai-analysis-8fd8" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            <T k="auto.generating-ai-powered-analysis-56c6" />
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* TLDR Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Zap className="h-5 w-5 text-orange-500" />
            <T k="ai.tldr" />
          </CardTitle>
        </CardHeader>
        <CardContent dir={isRTL ? 'rtl' : 'ltr'}>
          <p className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>{data.tldr}</p>
        </CardContent>
      </Card>

      {/* Key Points Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lightbulb className="h-5 w-5 text-rank-gold" />
            <T k="ai.keyPoints" />
          </CardTitle>
        </CardHeader>
        <CardContent dir={isRTL ? 'rtl' : 'ltr'}>
          <ul className={`space-y-2 ${isRTL ? 'pr-0' : ''}`}>
            {data.keyPoints.map((point, index) => (
              <li key={index} className={`text-sm text-muted-foreground flex items-start gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="text-byteline-green mt-0.5">•</span>
                <span className={isRTL ? 'text-right' : ''}>{point}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Simplified Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-blue-500" />
            <T k="ai.simplified" />
          </CardTitle>
        </CardHeader>
        <CardContent dir={isRTL ? 'rtl' : 'ltr'}>
          <p className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>{data.simplified}</p>
        </CardContent>
      </Card>

      {/* Challenge This Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <T k="ai.challengeThis" />
          </CardTitle>
        </CardHeader>
        <CardContent dir={isRTL ? 'rtl' : 'ltr'}>
          <p className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>{data.challengeThis}</p>
        </CardContent>
      </Card>
    </>
  );
};

export default TLDRWidget;
